import React, { useEffect, useState } from "react"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import FormHelperText from "@mui/material/FormHelperText"
import { useNavigate } from "react-router-dom"
import { useSnackbar } from "notistack"
import { useForm, Controller } from "react-hook-form"
import { addProgramLevel, editProgramLevel, getTierPrograms } from "services/program"
import { getProducts } from "services/products"
import { StyledProgramDialog } from "./Program.style"

const ProgramLevelDialog = (props) => {
  const { onSubmit, id } = props

  let navigate = useNavigate()

  const initProgramLevel = {
    programId: id,
    levelName: "",
    hideLevelLabel: false,
    // Tier/Product access is optional at the level. If unset, it inherits from the parent program.
    tierProgramId: null,
    productProgramId: null,
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      levelName: "",
      hideLevelLabel: false,
      tierProgramId: "",
      productProgramId: "",
    },
  })

  const { enqueueSnackbar } = useSnackbar()

  const [programLevel, setProgramLevel] = useState(initProgramLevel)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [tierPrograms, setTierPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [programAccess, setProgramAccess] = useState({
    tierProgramId: null,
    productProgramId: null,
  })

  useEffect(() => {
    if (!open) return
    ;(async () => {
      try {
        const tiers = await getTierPrograms()
        setTierPrograms(Array.isArray(tiers) ? tiers : [])
      } catch {
        setTierPrograms([])
      }
      try {
        const prod = await getProducts()
        setProducts(Array.isArray(prod) ? prod : [])
      } catch {
        setProducts([])
      }
    })()
  }, [open])

  const openDlg = (editProgramLevel, parentProgramAccess) => {
    reset()
    setProgramAccess({
      tierProgramId: parentProgramAccess?.tierProgramId ?? null,
      productProgramId: parentProgramAccess?.productProgramId ?? null,
    })
    const nextLevel = editProgramLevel
      ? {
          ...editProgramLevel,
          levelName: editProgramLevel.name,
          hideLevelLabel: !!editProgramLevel.hideLevelLabel,
        }
      : initProgramLevel
    setProgramLevel(nextLevel)
    // Set initial tier/product overrides. Empty = inherit from Program.
    setValue("levelName", nextLevel.levelName || "")
    setValue("hideLevelLabel", !!nextLevel.hideLevelLabel)
    setValue("tierProgramId", editProgramLevel?.tierProgramId ?? "")
    setValue("productProgramId", editProgramLevel?.productProgramId ?? "")
    setIsEditMode(editProgramLevel ? true : false)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleAction = async (data) => {
    setLoading(true)
    const tierProgramId =
      data?.tierProgramId === "" || typeof data?.tierProgramId === "undefined"
        ? null
        : Number(data.tierProgramId)
    const productProgramId =
      data?.productProgramId === "" ||
      typeof data?.productProgramId === "undefined"
        ? null
        : Number(data.productProgramId)
    const hideLevelLabel = !!data?.hideLevelLabel

    // Validation: if Tier Program is the special "product" type, a Product Gate must exist
    // either on the Level override or inherited from the Program.
    try {
      const selectedTier = tierPrograms.find(
        (t) => Number(t?.id) === Number(tierProgramId)
      )
      const isProductTier =
        selectedTier && String(selectedTier.type).toLowerCase() === "product"
      const effectiveProductProgramId =
        productProgramId ?? (programAccess?.productProgramId ?? null)
      if (isProductTier && !effectiveProductProgramId) {
        enqueueSnackbar(
          "Product Gate is required when Tier Program is set to Product (set it on this level or on the Program).",
          { variant: "error" }
        )
        setLoading(false)
        return
      }
    } catch {
      // ignore
    }

    const response = isEditMode
      ? await editProgramLevel({
          ...data,
          programId: initProgramLevel.programId,
          id: programLevel.id,
          tierProgramId,
          productProgramId,
          hideLevelLabel,
        })
      : await addProgramLevel({
          ...data,
          programId: initProgramLevel.programId,
          tierProgramId,
          productProgramId,
          hideLevelLabel,
        })

    if (response.data.rtnCode === 401) {
      localStorage.clear()
      navigate("/login")
    }

    if (response.data.rtnCode === 200) {
      enqueueSnackbar(
        isEditMode ? "Update Level Success" : "Create Level Success",
        { variant: "success" }
      )
    } else {
      enqueueSnackbar(isEditMode ? "Update Level Fail" : "Create Level Fail", {
        variant: "warning",
      })
    }
    setLoading(false)
    onSubmit()
    setOpen(false)
  }

  const dlg = (
    <StyledProgramDialog open={open} fullWidth={true} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">
              {isEditMode ? "Edit Program Level" : "Add Program Level"}
            </h2>
            <p className="dialog-subtitle">
              Configure level metadata and optional tier/product access
              overrides.
            </p>
          </div>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <TextField
              autoFocus
              autoComplete="off"
              margin="dense"
              id="program-level-name"
              label="Level Label"
              fullWidth
              placeholder="e.g. Level 1"
              {...register("levelName", { required: true })}
              error={!!errors?.levelName}
              helperText={
                !!errors?.levelName
                  ? "* Required"
                  : "Shown as the section header in apps (e.g. “Level 1”). Customize per section."
              }
              variant="standard"
            />

            <FormControlLabel
              sx={{ mt: 1, mb: 1, alignItems: "flex-start" }}
              control={
                <Controller
                  name="hideLevelLabel"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Hide Level Label</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When checked, apps will not show this level’s label (useful
                    for BMC replays and similar programs).
                  </Typography>
                </Box>
              }
            />

            {/* Hidden fields managed by Selects below */}
            <input type="hidden" {...register("tierProgramId")} />
            <input type="hidden" {...register("productProgramId")} />

            <Box sx={{ mt: 2 }}>
              <Typography className="control-label" gutterBottom>
                Tier / Product Access
              </Typography>
              <FormControl fullWidth margin="dense">
                <InputLabel id="level-tier-program-select-label">
                  Tier Program
                </InputLabel>
                <Select
                  labelId="level-tier-program-select-label"
                  id="level-tier-program-select"
                  label="Tier Program"
                  value={watch("tierProgramId") ?? ""}
                  onChange={(e) => setValue("tierProgramId", e.target.value)}
                >
                  <MenuItem value="">
                    {(() => {
                      const programTier = tierPrograms.find(
                        (t) =>
                          Number(t.id) === Number(programAccess.tierProgramId)
                      )
                      const programTierType = programTier?.type || "None"
                      const prod = products.find(
                        (p) =>
                          Number(p.programId) ===
                          Number(programAccess.productProgramId)
                      )
                      const prodLabel = prod?.name || "None"
                      return `Inherit from Program (Tier: ${programTierType}, Product Gate: ${prodLabel})`
                    })()}
                  </MenuItem>
                  {[...tierPrograms]
                    .sort((a, b) => {
                      const order = {
                        Free: 0,
                        Silver: 1,
                        Gold: 2,
                        Platinum: 3,
                        product: 4,
                      }
                      const aIdx = order[a?.type] ?? 99
                      const bIdx = order[b?.type] ?? 99
                      return aIdx - bIdx
                    })
                    .map((tier) => (
                      <MenuItem key={tier.id} value={tier.id}>
                        {String(tier.type).toLowerCase() === "product"
                          ? "Product (select below)"
                          : tier.type}
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                  Leave this as “Inherit from Program” to use the same access
                  rules as the parent program.
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth margin="dense">
                <InputLabel id="level-product-select-label">
                  Product Gate
                </InputLabel>
                <Select
                  labelId="level-product-select-label"
                  id="level-product-select"
                  label="Product Gate"
                  value={watch("productProgramId") ?? ""}
                  onChange={(e) => setValue("productProgramId", e.target.value)}
                >
                  <MenuItem value="">
                    {(() => {
                      const prod = products.find(
                        (p) =>
                          Number(p.programId) ===
                          Number(programAccess.productProgramId)
                      )
                      const prodLabel = prod?.name || "None"
                      return `Inherit from Program (${prodLabel})`
                    })()}
                  </MenuItem>
                  {products.map((p) => (
                    <MenuItem key={p.programId} value={p.programId}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Optional. Set this if you want this level to be accessible by
                  a specific Product Gate in addition to (or instead of) the
                  Tier.
                </FormHelperText>
              </FormControl>
            </Box>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            autoFocus
            variant="outlined"
            size="medium"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            className="dialog-submit-btn"
            type="submit"
            variant="contained"
            size="medium"
            autoFocus
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </StyledProgramDialog>
  )

  return [openDlg, dlg, loading]
}

export default ProgramLevelDialog
